import {
    Button,
    Link,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material'
import React from 'react'
import { Archive, Check, Repeat } from 'react-feather'
import { NavLink } from 'react-router-dom'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { OutlinedIconButton } from './OutlinedIconButton'
import { PRInputEntity } from '@finance-lab/utils/types'
import { CompanyEntity } from 'types/CompanyEntity'
import { getPressReleaseLink } from '../../../utils/get-press-release-link'

export type PRTableProps = {
    onArchive?: (pr: PRInputEntity) => Promise<void>
    onBlock?: (pr: PRInputEntity) => Promise<void>
    onUnarchive?: (pr: PRInputEntity) => void
    prs: PRInputEntity[] | null
    selectedPR?: PRInputEntity | null
    setSelectedCompany?: (company: Pick<CompanyEntity, 'id' | 'name'>) => void
    setSelectedPR?: (pr: PRInputEntity | null) => void
}

export const PRTable = ({
    prs,
    onArchive,
    onUnarchive,
    setSelectedCompany: selectTargetCompany,
    selectedPR,
    setSelectedPR,
}: PRTableProps) => {
    const theme = useTheme()
    const onSelectPR =
        setSelectedPR &&
        selectTargetCompany &&
        ((pr: PRInputEntity) => {
            // select the PR
            setSelectedPR(pr)

            // if the PR has a company, select the company
            if (pr.companyId) {
                selectTargetCompany({ id: pr.companyId, name: pr.companyName })
            }
        })

    if (prs === null || prs === undefined) {
        return (
            <>
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
                <Skeleton variant='rectangular' height={24 * 4} sx={{ my: 6 }} />
            </>
        )
    }
    if (prs.length === 0) {
        return <Typography variant='body1'>Inga pressmeddelanden hittades</Typography>
    }

    const rows = prs.map((pr) => (
        <TableRow
            key={pr.id}
            sx={{
                backgroundColor: pr.id === selectedPR?.id ? theme.palette.rowColor.main : null,
            }}
        >
            <TableCell>
                <Stack spacing={2}>
                    <Typography variant='caption'>
                        {formatDateYYYYMMDD(pr.publishedAt)} {pr.companyName}
                    </Typography>
                    <Link component={NavLink} to={getPressReleaseLink(pr.companyId, pr.id)} target='_blank'>
                        {pr.title || pr.id || 'Titel saknas'}
                    </Link>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack spacing={2}>
                    {onSelectPR && (
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={() => onSelectPR(pr)}
                            endIcon={<Check size={16} />}
                        >
                            Välj
                        </Button>
                    )}
                    {onArchive && (
                        <Button
                            variant='outlined'
                            color='primary'
                            size='small'
                            onClick={() => void onArchive(pr)}
                            endIcon={<Archive size={16} />}
                        >
                            Arkiv
                        </Button>
                    )}

                    {onUnarchive && (
                        <Tooltip title='Återställ'>
                            <OutlinedIconButton color='primary' size='small' onClick={() => onUnarchive(pr)}>
                                <Repeat size={16} />
                            </OutlinedIconButton>
                        </Tooltip>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    ))

    return (
        <>
            <TableContainer>
                <Table>
                    <TableBody>{rows}</TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
